/**
 * File-type helpers that tolerate mobile (esp. Android) file pickers.
 *
 * Android's Storage Access Framework -- particularly the Downloads provider --
 * frequently reports a file's MIME type as "" or "application/octet-stream",
 * even for a plain .mp4. Two failures follow from that, both of which these
 * helpers defend against:
 *
 *   1. An `accept` attribute built from explicit MIME subtypes (e.g.
 *      "video/mp4,video/quicktime") makes the picker GREY OUT the file, so the
 *      user can't select it at all. `buildAcceptAttribute` emits category
 *      wildcards (video/*, image/*, audio/*) plus extensions, which the picker
 *      honors regardless of how it resolved the MIME type.
 *
 *   2. Post-selection validation that does `allowedMimeTypes.includes(file.type)`
 *      rejects the file. `isFileTypeAllowed` falls back to the filename
 *      extension when the OS reports a generic/empty type.
 *
 * The server re-validates every upload, so the extension fallback only widens
 * what the client will *attempt*, never what ultimately gets accepted.
 */

// MIME values the OS reports when it can't (or won't) resolve the real type.
const GENERIC_MIME_TYPES = new Set(["", "application/octet-stream", "application/unknown"])

// Allowed MIME subtype -> the extensions that legitimately carry it. Drives
// both the picker `accept` hint and the extension fallback in validation.
const MIME_EXTENSIONS: Record<string, readonly string[]> = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/jpg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "video/mp4": [".mp4", ".m4v"],
  "video/quicktime": [".mov", ".qt"],
  "video/webm": [".webm"],
  "video/mpeg": [".mpeg", ".mpg"],
  "audio/mpeg": [".mp3"],
  "audio/mp3": [".mp3"],
  "audio/aac": [".aac"],
  "audio/wav": [".wav"],
  "audio/x-wav": [".wav"],
  "audio/flac": [".flac"],
}

const isGenericMimeType = (type: string): boolean => GENERIC_MIME_TYPES.has(type)

/** Lower-cased extension including the leading dot, or "" when there is none. */
export const getFileExtension = (filename: string): string => {
  const dot = filename.lastIndexOf(".")
  return dot >= 0 ? filename.slice(dot).toLowerCase() : ""
}

/** Distinct extensions mapped to any of the given MIME types. */
export const extensionsForMimeTypes = (mimeTypes: readonly string[]): string[] => {
  const exts = new Set<string>()
  for (const mime of mimeTypes) {
    for (const ext of MIME_EXTENSIONS[mime] ?? []) exts.add(ext)
  }
  return [...exts]
}

/**
 * Build an `accept` attribute that mobile pickers honor: category wildcards
 * (video/*, image/*, audio/*) plus explicit extensions. Avoids the Android
 * picker greying out files whose MIME the OS reports as octet-stream.
 */
export const buildAcceptAttribute = (mimeTypes: readonly string[]): string => {
  const categories = new Set<string>()
  for (const mime of mimeTypes) {
    const slash = mime.indexOf("/")
    if (slash > 0) categories.add(`${mime.slice(0, slash)}/*`)
  }
  return [...categories, ...extensionsForMimeTypes(mimeTypes)].join(",")
}

/**
 * Whether a selected file is one of the allowed MIME types. Falls back to the
 * filename extension when the OS reports a generic/empty MIME (Android). A
 * concrete-but-unlisted MIME (e.g. "video/x-matroska") is still rejected.
 */
export const isFileTypeAllowed = (file: File, allowedMimeTypes: readonly string[]): boolean => {
  if (allowedMimeTypes.includes(file.type)) return true
  if (!isGenericMimeType(file.type)) return false
  const ext = getFileExtension(file.name)
  return ext !== "" && extensionsForMimeTypes(allowedMimeTypes).includes(ext)
}

// Extensions per top-level category, derived from MIME_EXTENSIONS so there's a
// single source of truth (no hand-maintained parallel list to drift).
const extensionsForCategory = (category: string): string[] =>
  extensionsForMimeTypes(Object.keys(MIME_EXTENSIONS).filter(m => m.startsWith(`${category}/`)))

const VIDEO_EXTENSIONS = extensionsForCategory("video")
const IMAGE_EXTENSIONS = extensionsForCategory("image")
const AUDIO_EXTENSIONS = extensionsForCategory("audio")

// Classify a picked file by top-level category for upload routing, tolerating
// Android's empty/generic MIME by falling back to the extension. Used to send a
// file to the correct pipeline (video vs image vs audio) rather than dropping
// it when the OS reports no MIME.
const looksLikeCategory = (file: File, category: string, extensions: string[]): boolean => {
  if (file.type.startsWith(`${category}/`)) return true
  if (!isGenericMimeType(file.type)) return false
  return extensions.includes(getFileExtension(file.name))
}

export const looksLikeVideoFile = (file: File): boolean =>
  looksLikeCategory(file, "video", VIDEO_EXTENSIONS)

export const looksLikeImageFile = (file: File): boolean =>
  looksLikeCategory(file, "image", IMAGE_EXTENSIONS)

export const looksLikeAudioFile = (file: File): boolean =>
  looksLikeCategory(file, "audio", AUDIO_EXTENSIONS)
